import React, { createContext, useContext, useReducer, useEffect, ReactNode } from 'react';
import api from '../services/api';

// Types
interface User {
  _id: string;
  firstName: string;
  lastName: string;
  name: string;
  email: string;
  phone: string;
  role: string;
  department: string;
  location: string;
  locationId: string;
  queueIds: string[];
  extension: string;
  sipExtension?: string;
  sipPassword?: string;
  status: string;
  agentStatus: string;
  currentStatusId: string;
  lastStatusChange: string;
  lastLogin?: string;
  permissions: string[];
  sipNumbers: string[];
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

interface UserState {
  users: User[];
  currentUser: User | null;
  authUser: User | null;
  loading: boolean;
  error: string | null;
}

interface UserAction {
  type: string;
  payload?: any;
}

interface UserContextType {
  users: User[];
  currentUser: User | null;
  authUser: User | null;
  loading: boolean;
  error: string | null;
  fetchUsers: () => Promise<void>;
  fetchUser: (id: string) => Promise<User | null>;
  createUser: (userData: Partial<User>) => Promise<User | null>;
  updateUser: (id: string, userData: Partial<User>) => Promise<User | null>;
  deleteUser: (id: string) => Promise<boolean>;
  updateUserStatus: (id: string, status: string) => Promise<User | null>;
  updateUserQueues: (id: string, queueIds: string[]) => Promise<User | null>;
  getUsersByLocation: (locationId: string) => Promise<User[]>;
  setUser: (user: User | null) => void;
  getUsersByRole: (role: string) => Promise<User[]>;
  setCurrentUser: (user: User) => void;
  setAuthUser: (user: User) => void;
  clearError: () => void;
}

// User Context
const UserContext = createContext<UserContextType | undefined>(undefined);

// User Reducer
const userReducer = (state: UserState, action: UserAction): UserState => {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, loading: action.payload };
    case 'SET_USERS':
      return { ...state, users: action.payload, loading: false };
    case 'SET_CURRENT_USER':
      return { ...state, currentUser: action.payload };
    case 'SET_AUTH_USER':
      return { ...state, authUser: action.payload };
    case 'ADD_USER':
      return { ...state, users: [...state.users, action.payload] };
    case 'UPDATE_USER':
      return {
        ...state,
        users: state.users.map((user: User) =>
          user._id === action.payload._id ? action.payload : user
        )
      };
    case 'DELETE_USER':
      return {
        ...state,
        users: state.users.filter((user: User) => user._id !== action.payload)
      };
    case 'SET_ERROR':
      return { ...state, error: action.payload, loading: false };
    case 'CLEAR_ERROR':
      return { ...state, error: null };
    default:
      return state;
  }
};

// Initial State
const initialState = {
  users: [],
  currentUser: null,
  authUser: null,
  loading: false,
  error: null
};

// User Provider
interface UserProviderProps {
  children: ReactNode;
}

export const UserProvider = ({ children }: UserProviderProps) => {
  const [state, dispatch] = useReducer(userReducer, initialState);


  // Set loading
  const setLoading = (loading: boolean) => {
    dispatch({ type: 'SET_LOADING', payload: loading });
  };

  // Set error
  const setError = (error: string) => {
    dispatch({ type: 'SET_ERROR', payload: error });
  };

  // Clear error
  const clearError = () => {
    dispatch({ type: 'CLEAR_ERROR' });
  };

  // Fetch all users
  const fetchUsers = async (filters = {}) => {
    try {
      setLoading(true);
      clearError();

      const params = new URLSearchParams(filters);
      
      const response = await api.get(`/users?${params}`);

      if (response.data.success) {
        dispatch({ type: 'SET_USERS', payload: response.data.data });
      } else {
        setError(response.data.message || 'Kullanıcılar getirilemedi');
      }
    } catch (error) {
      console.error('Kullanıcılar getirme hatası:', error);
      setError((error as any).response?.data?.message || 'Kullanıcılar getirilemedi');
    }
  };

  // Fetch single user
  const fetchUser = async (id: string) => {
    try {
      setLoading(true);
      clearError();

      const response = await api.get(`/users/${id}`);

      if (response.data.success) {
        dispatch({ type: 'SET_CURRENT_USER', payload: response.data.data });
        return response.data.data;
      } else {
        setError(response.data.message || 'Kullanıcı getirilemedi');
        return null;
      }
    } catch (error) {
      console.error('Kullanıcı getirme hatası:', error);
      setError((error as any).response?.data?.message || 'Kullanıcı getirilemedi');
      return null;
    }
  };

  // Create user
  const createUser = async (userData: Partial<User>) => {
    try {
      setLoading(true);
      clearError();

      const response = await api.post(`/users`, userData);

      if (response.data.success) {
        dispatch({ type: 'ADD_USER', payload: response.data.data });
        return response.data.data;
      } else {
        setError(response.data.message || 'Kullanıcı oluşturulamadı');
        return null;
      }
    } catch (error) {
      console.error('Kullanıcı oluşturma hatası:', error);
      setError((error as any).response?.data?.message || 'Kullanıcı oluşturulamadı');
      return null;
    }
  };

  // Update user
  const updateUser = async (id: string, userData: Partial<User>) => {
    try {
      setLoading(true);
      clearError();

      const response = await api.put(`/users/${id}`, userData);

      if (response.data.success) {
        dispatch({ type: 'UPDATE_USER', payload: response.data.data });
        return response.data.data;
      } else {
        setError(response.data.message || 'Kullanıcı güncellenemedi');
        return null;
      }
    } catch (error) {
      console.error('Kullanıcı güncelleme hatası:', error);
      setError((error as any).response?.data?.message || 'Kullanıcı güncellenemedi');
      return null;
    }
  };

  // Delete user
  const deleteUser = async (id: string) => {
    try {
      setLoading(true);
      clearError();

      const response = await api.delete(`/users/${id}`);

      if (response.data.success) {
        dispatch({ type: 'DELETE_USER', payload: id });
        return true;
      } else {
        setError(response.data.message || 'Kullanıcı silinemedi');
        return false;
      }
    } catch (error) {
      console.error('Kullanıcı silme hatası:', error);
      setError((error as any).response?.data?.message || 'Kullanıcı silinemedi');
      return false;
    }
  };

  // Update user status
  const updateUserStatus = async (id: string, status: string) => {
    try {
      setLoading(true);
      clearError();

      const response = await api.put(`/users/${id}/status`, {
        status
      });

      if (response.data.success) {
        // Update the user in state
        const updatedUser = response.data.data;
        dispatch({ type: 'UPDATE_USER', payload: updatedUser });
        return updatedUser;
      } else {
        setError(response.data.message || 'Kullanıcı durumu güncellenemedi');
        return null;
      }
    } catch (error) {
      console.error('Kullanıcı durumu güncelleme hatası:', error);
      setError((error as any).response?.data?.message || 'Kullanıcı durumu güncellenemedi');
      return null;
    }
  };

  // Update user queues
  const updateUserQueues = async (id: string, queueIds: string[]) => {
    try {
      setLoading(true);
      clearError();

      const response = await api.put(`/users/${id}/queues`, {
        queueIds
      });

      if (response.data.success) {
        // Update the user in state
        const updatedUser = response.data.data;
        dispatch({ type: 'UPDATE_USER', payload: updatedUser });
        return updatedUser;
      } else {
        setError(response.data.message || 'Kullanıcı kuyrukları güncellenemedi');
        return null;
      }
    } catch (error) {
      console.error('Kullanıcı kuyrukları güncelleme hatası:', error);
      setError((error as any).response?.data?.message || 'Kullanıcı kuyrukları güncellenemedi');
      return null;
    }
  };

  // Get users by location
  const getUsersByLocation = async (locationId: string) => {
    try {
      const response = await api.get(`/users?locationId=${locationId}`);

      if (response.data.success) {
        return response.data.data;
      } else {
        setError(response.data.message || 'Lokasyon kullanıcıları getirilemedi');
        return [];
      }
    } catch (error) {
      console.error('Lokasyon kullanıcıları getirme hatası:', error);
      setError((error as any).response?.data?.message || 'Lokasyon kullanıcıları getirilemedi');
      return [];
    }
  };

  // Get users by role
  const getUsersByRole = async (role: string) => {
    try {
      const response = await api.get(`/users?role=${role}`);

      if (response.data.success) {
        return response.data.data;
      } else {
        setError(response.data.message || 'Rol kullanıcıları getirilemedi');
        return [];
      }
    } catch (error) {
      console.error('Rol kullanıcıları getirme hatası:', error);
      setError((error as any).response?.data?.message || 'Rol kullanıcıları getirilemedi');
      return [];
    }
  };

  // Set current user
  const setCurrentUser = (user: User) => {
    dispatch({ type: 'SET_CURRENT_USER', payload: user });
  };

  // Set auth user
  const setAuthUser = (user: User) => {
    dispatch({ type: 'SET_AUTH_USER', payload: user });
  };

  // Set user function
  const setUser = (user: User | null) => {
    dispatch({ type: 'SET_AUTH_USER', payload: user });
    if (user) {
      localStorage.setItem('user', JSON.stringify(user));
    } else {
      localStorage.removeItem('user');
    }
  };

  // Context value
  const value = {
    ...state,
    fetchUsers,
    fetchUser,
    createUser,
    updateUser,
    deleteUser,
    updateUserStatus,
    updateUserQueues,
    getUsersByLocation,
    getUsersByRole,
    setCurrentUser,
    setAuthUser,
    setUser,
    setLoading,
    setError,
    clearError
  };

  // Load user from storage on mount
  useEffect(() => {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      try {
        const user = JSON.parse(userStr);
        dispatch({ type: 'SET_AUTH_USER', payload: user });
      } catch (error) {
        console.error('Kullanıcı bilgisi parse hatası:', error);
        localStorage.removeItem('user');
        dispatch({ type: 'SET_AUTH_USER', payload: null });
      }
    }
  }, []);

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  );
};

// User Hook
export const useUser = (): UserContextType => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};

export default UserContext;
